import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from 'src/api/client';
import {
  ForgotPasswordRequest,
  GoogleCredentialRequest,
  GoogleRegistrationCompleteRequest,
  LoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
} from 'src/api/types';
import { ProfileListResponse } from 'src/api/types';
import { getErrorMessage } from 'src/utils/errors/getErrorMessage';
import {
  clearGoogleRegistration,
  saveGoogleRegistration,
} from 'src/utils/googleRegistration';

const saveTokenPair = (access: string, refresh: string) => {
  localStorage.setItem('access_token', access);
  localStorage.setItem('refresh_token', refresh);
};

interface User {
  id: number;
  email: string;
  picture: string;
  first_name: string;
  last_name: string;
  type: string;
  state_id: number;
  date_birth: string;
  phone: string;
}

interface AuthState {
  user: Partial<User> | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: (() => {
    const userProfile = localStorage.getItem('userProfile');
    return userProfile ? JSON.parse(userProfile) : null;
  })(),
  isAuthenticated: !!localStorage.getItem('access_token'),
  loading: false,
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (data: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.login(data);
      saveTokenPair(response.access, response.refresh);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Login failed'));
    }
  }
);

export const authenticateGoogle = createAsyncThunk(
  'auth/authenticateGoogle',
  async (data: GoogleCredentialRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.authenticateGoogle(data);
      if (response.status === 'authenticated') {
        saveTokenPair(response.access, response.refresh);
        clearGoogleRegistration();
      } else {
        saveGoogleRegistration({
          registrationToken: response.registration_token,
          email: response.email,
        });
      }
      return response;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Google sign-in failed'));
    }
  }
);

export const completeGoogleRegistration = createAsyncThunk(
  'auth/completeGoogleRegistration',
  async (data: GoogleRegistrationCompleteRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.completeGoogleRegistration(data);
      saveTokenPair(response.access, response.refresh);
      clearGoogleRegistration();
      return response;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Google registration failed'));
    }
  }
);

export const profile = createAsyncThunk(
  'auth/profile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.getProfile();
      return response;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Get profile failed'));
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (data: RegisterRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.register(data);
      // Токены не сохраняем — пользователь не авторизован до верификации почты
      return response;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Registration failed'));
    }
  }
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (uuid: string, { rejectWithValue }) => {
    try {
      await apiClient.verifyEmail(uuid);
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Email verification failed'));
    }
  }
);

export const resendVerification = createAsyncThunk(
  'auth/resendVerification',
  async (data: ForgotPasswordRequest, { rejectWithValue }) => {
    try {
      await apiClient.resendVerification(data);
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Resend verification failed'));
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (data: ForgotPasswordRequest, { rejectWithValue }) => {
    try {
      await apiClient.forgotPassword(data);
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Password reset request failed'));
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ uuid, data }: { uuid: string; data: ResetPasswordRequest }, { rejectWithValue }) => {
    try {
      await apiClient.resetPassword(uuid, data);
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Password reset failed'));
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
  logout: (state) => {
    state.user = null;
    state.isAuthenticated = false;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('userProfile');
    localStorage.removeItem('selectedClient');
    clearGoogleRegistration();
  },
  clearError: (state) => {
    state.error = null;
  },
  updateUser: (state, action: PayloadAction<ProfileListResponse>) => {
    state.user = action.payload;
    localStorage.setItem('userProfile', JSON.stringify(action.payload));
  },
},
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state) => {
      state.loading = false;
      state.isAuthenticated = true;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(profile.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(profile.fulfilled, (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = {
        email: action.payload.email,
        id: action.payload.id,
        picture: action.payload.picture,
        first_name: action.payload.first_name,
        last_name: action.payload.last_name,
        type: action.payload.type,
        state_id: action.payload.state_id,
        date_birth: action.payload.date_birth
      };

      localStorage.setItem('userProfile', JSON.stringify(action.payload));
    });
    builder.addCase(register.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(register.fulfilled, (state) => {
      state.loading = false;
      // isAuthenticated остаётся false — пользователь должен верифицировать почту и залогиниться
    });
    builder.addCase(register.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(verifyEmail.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(verifyEmail.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(verifyEmail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(forgotPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(forgotPassword.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(forgotPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(resetPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(resetPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { logout, clearError, updateUser } = authSlice.actions;
export default authSlice.reducer;
